import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, DollarSign, Clock, CheckCircle, CreditCard,
  RefreshCw, Loader2, ChevronLeft, ChevronRight, Send, Ban
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, paid: 0 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [etaDays, setEtaDays] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  const loadPayouts = useCallback(async (p = 1, status = statusFilter) => {
    setLoading(true);
    try {
      const qs = status ? `&status=${status}` : '';
      const res = await fetch(`${API}/affiliate/admin/payouts?page=${p}&limit=20${qs}`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setPayouts(d.payouts || []);
        setTotal(d.total || 0);
        setCounts(d.counts || { pending: 0, approved: 0, paid: 0 });
        setPage(d.page || 1);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers, statusFilter]);

  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const generatePayouts = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/payouts/generate`, { method: 'POST', headers: headers() });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message);
        loadPayouts(1, '');
      } else { toast.error('Failed to generate payouts'); }
    } catch { toast.error('Error generating payouts'); }
    setGenerating(false);
  };

  const batchApprove = async () => {
    if (selected.length === 0) { toast.error('Select payouts to approve'); return; }
    setApproving(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/payouts/batch-approve`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ payout_ids: selected, eta_days: etaDays }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message);
        setSelected([]);
        loadPayouts(1, '');
      } else { toast.error('Failed to approve payouts'); }
    } catch { toast.error('Error approving payouts'); }
    setApproving(false);
  };

  const markPaid = async (payoutId) => {
    try {
      const res = await fetch(`${API}/affiliate/admin/payouts/${payoutId}/mark-paid`, { method: 'POST', headers: headers() });
      if (res.ok) { toast.success('Payout marked as paid'); loadPayouts(page, statusFilter); }
      else { toast.error('Failed to mark as paid'); }
    } catch { toast.error('Error'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllPending = () => {
    const pendingIds = payouts.filter(p => p.status === 'pending').map(p => p.payout_id);
    setSelected(prev => prev.length === pendingIds.length ? [] : pendingIds);
  };

  const statusBadge = (s) => {
    const map = {
      pending: 'bg-amber-50 text-amber-700',
      approved: 'bg-blue-50 text-blue-700',
      paid: 'bg-green-50 text-green-700',
    };
    return <Badge className={`text-xs capitalize ${map[s] || 'bg-gray-50 text-gray-600'}`}>{s}</Badge>;
  };

  const totalPages = Math.ceil(total / 20);
  const tabs = [
    { id: '', label: 'All', count: counts.pending + counts.approved + counts.paid },
    { id: 'pending', label: 'Pending', count: counts.pending, icon: Clock },
    { id: 'approved', label: 'Approved', count: counts.approved, icon: CheckCircle },
    { id: 'paid', label: 'Paid', count: counts.paid, icon: CreditCard },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-payouts-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div>
            <h1 className="text-xl font-bold text-[#0f1f33]">Payout Processing</h1>
            <p className="text-sm text-gray-400">Generate, approve, and track affiliate payouts.</p>
          </div>
        </div>
        <Button onClick={generatePayouts} disabled={generating} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="generate-payouts-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Generate Payouts
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-3.5 h-3.5 text-amber-600" /></div>
            <span className="text-xs text-gray-400">Pending</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{counts.pending}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center"><CheckCircle className="w-3.5 h-3.5 text-blue-600" /></div>
            <span className="text-xs text-gray-400">Approved</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{counts.approved}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center"><CreditCard className="w-3.5 h-3.5 text-green-600" /></div>
            <span className="text-xs text-gray-400">Paid</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{counts.paid}</p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-50 p-1 rounded-xl">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setStatusFilter(t.id); setSelected([]); loadPayouts(1, t.id); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === t.id ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`payouts-tab-${t.id || 'all'}`}>
            {t.label} <span className="text-xs text-gray-400">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Batch Approve Bar */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between p-4 mb-4 bg-blue-50 rounded-xl border border-blue-200" data-testid="batch-approve-bar">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#0f1f33]">{selected.length} payout(s) selected</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">ETA (days):</label>
              <input type="number" value={etaDays} onChange={e => setEtaDays(parseInt(e.target.value) || 5)} min={1} max={30}
                className="w-16 h-8 rounded-md border border-gray-200 px-2 text-sm text-center" data-testid="eta-days-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="clear-selection-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Clear</Button>
            <Button size="sm" onClick={batchApprove} disabled={approving} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="batch-approve-btn">
              {approving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Approve & Notify
            </Button>
          </div>
        </div>
      )}

      {/* Payouts List */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>
      ) : payouts.length === 0 ? (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-12 text-center">
            <DollarSign className="w-12 h-12 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0f1f33] mb-2">No payouts found</h3>
            <p className="text-sm text-gray-400">Click "Generate Payouts" to create pending payouts from approved conversions.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {statusFilter === 'pending' && payouts.some(p => p.status === 'pending') && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={selectAllPending} className="text-xs" data-testid="select-all-pending">
                {selected.length === payouts.filter(p => p.status === 'pending').length ? 'Deselect All' : 'Select All Pending'}
              </Button>
            </div>
          )}
          {payouts.map((p, i) => (
            <div key={i} className={`flex items-center gap-4 p-4 bg-white rounded-xl border transition-all ${selected.includes(p.payout_id) ? 'border-blue-300 bg-blue-50/30' : 'border-gray-100'} shadow-sm`}
              data-testid={`payout-${p.payout_id}`}>
              {p.status === 'pending' && (
                <input type="checkbox" checked={selected.includes(p.payout_id)} onChange={() => toggleSelect(p.payout_id)}
                  className="w-4 h-4 rounded border-gray-300" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-[#0f1f33]">{p.partner_name || 'Unknown Partner'}</p>
                  {statusBadge(p.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {p.partner_email && <span>{p.partner_email}</span>}
                  <span>Created: {p.created_at?.slice(0, 10)}</span>
                  {p.eta_date && <span>ETA: {p.eta_date}</span>}
                  {p.processed_at && <span>Paid: {p.processed_at?.slice(0, 10)}</span>}
                </div>
              </div>
              <div className="text-right flex items-center gap-4">
                <p className="text-lg font-bold text-emerald-600">{fmt(p.amount)}</p>
                {p.status === 'approved' && (
                  <Button size="sm" variant="outline" onClick={() => markPaid(p.payout_id)} data-testid={`mark-paid-${p.payout_id}`}>
                    <CreditCard className="w-3.5 h-3.5 mr-1" /> Mark Paid
                  </Button>
                )}
              </div>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => loadPayouts(page - 1, statusFilter)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => loadPayouts(page + 1, statusFilter)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
